"use client";

import React from "react";
import { MapPin, Phone, Mail, Facebook, Linkedin, Twitter, Instagram, Loader } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function ConnectForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div data-section-id="17495" className="flex flex-col items-center justify-center h-full min-h-[400px] p-8 text-center bg-[#F89406]/5 rounded-[4px] border border-[#F89406]/20 animate-in fade-in zoom-in duration-500">
        <div className="w-16 h-16 bg-[#F89406]/10 text-[#F89406] rounded-full flex items-center justify-center mb-4">
          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-8 h-8">
            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
          </svg>
        </div>
        <h3 className="text-2xl font-bold text-[#343434] mb-2 font-sans">Message Received</h3>
        <p className="text-[#343434]/70 font-sans">{successMessage}</p>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-5">
      <FormField control={control} name="name" rules={{
      required: "Name is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#343434] font-medium font-sans">Full Name</FormLabel>
            <FormControl>
              <input {...field} className="flex h-12 w-full rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#F89406] focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 font-sans text-[#343434]" placeholder="Enter your name" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="email" rules={{
      required: "Email is required",
      pattern: {
        value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
        message: "Invalid email address"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#343434] font-medium font-sans">Email Address</FormLabel>
            <FormControl>
              <input {...field} type="email" className="flex h-12 w-full rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#F89406] focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 font-sans text-[#343434]" placeholder="email@company.com" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="phone" rules={{
      pattern: {
        value: /^[+]?[(]?[0-9]{3}[)]?[-\s.]?[0-9]{3}[-\s.]?[0-9]{4,6}$/,
        message: "Invalid phone number"
      }
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#343434] font-medium font-sans">Phone Number</FormLabel>
            <FormControl>
              <input {...field} type="tel" className="flex h-12 w-full rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#F89406] focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 font-sans text-[#343434]" placeholder="+45 00 00 00 00" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      <FormField control={control} name="message" rules={{
      required: "Message is required"
    }} render={({
      field
    }) => <FormItem>
            <FormLabel className="text-[#343434] font-medium font-sans">Project Inquiry</FormLabel>
            <FormControl>
              <textarea {...field} rows={4} className="flex min-h-[120px] w-full rounded-[4px] border border-slate-200 bg-slate-50 px-4 py-3 text-sm ring-offset-background placeholder:text-muted-foreground focus-visible:outline-none focus-visible:border-[#F89406] focus-visible:ring-0 disabled:cursor-not-allowed disabled:opacity-50 transition-all duration-200 resize-y font-sans text-[#343434]" placeholder="How can Vantage Support Group assist your operations?" />
            </FormControl>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-3 text-sm text-red-600 bg-red-50 rounded-[4px] border border-red-100 font-sans">
          {submitError}
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full rounded-[3px] bg-[#F89406] hover:bg-[#F89406]/90 text-white font-bold py-6 text-base shadow-[0px_2px_2px_0px_rgba(248,148,6,0.14),0px_3px_1px_-2px_rgba(248,148,6,0.2),0px_1px_5px_0px_rgba(248,148,6,0.12)] transition-all duration-300 mt-2 font-sans">
        {isSubmitting ? <>
            <Loader className="mr-2 h-5 w-5 animate-spin" />
            Processing...
          </> : "Submit Inquiry"}
      </Button>
    </Form>;
}
export default function ConnectWithUs() {
  const {
    ref: sectionRef,
    isVisible
  } = useRevealOnScroll();
  return <section id="direct-dialogue" ref={sectionRef} className="relative w-full py-20 lg:py-32 bg-[#0F172A] overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[600px] h-[600px] bg-[#F89406]/5 rounded-full blur-3xl" />
        <div className="absolute bottom-[10%] -left-[10%] w-[400px] h-[400px] bg-white/5 rounded-full blur-3xl" />
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {}
          <div className={`space-y-10 transition-all duration-700 delay-100 ${isVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-10"}`}>
            <div className="space-y-4">
              <h2 className="text-4xl md:text-5xl font-bold font-sans text-white tracking-tight">Contact</h2>
              <p className="text-lg text-white/80 max-w-md leading-relaxed font-sans">Connect directly with our team.</p>
            </div>

            <div className="space-y-8">
              {}
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-[4px] bg-[#F89406]/10 flex items-center justify-center text-[#F89406] group-hover:bg-[#F89406] group-hover:text-white transition-colors duration-300">
                  <MapPin className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-sans text-white mb-1">Headquarters</h3>
                  <p className="text-white/70 font-sans">Møllevej 26C 5300 Kerteminde, Denmark</p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-[4px] bg-[#F89406]/10 flex items-center justify-center text-[#F89406] group-hover:bg-[#F89406] group-hover:text-white transition-colors duration-300">
                  <Phone className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-sans text-white mb-1">Direct Line</h3>
                  <p className="text-white/70 font-sans">+45 51 19 40 45</p>
                  <p className="text-sm text-white/50 mt-1 font-sans">Available Mon-Fri: 08:00 - 17:00</p>
                </div>
              </div>

              {}
              <div className="flex items-start gap-5 group">
                <div className="flex-shrink-0 w-12 h-12 rounded-[4px] bg-[#F89406]/10 flex items-center justify-center text-[#F89406] group-hover:bg-[#F89406] group-hover:text-white transition-colors duration-300">
                  <Mail className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="text-xl font-bold font-sans text-white mb-1">Digital Correspondence</h3>
                  <p className="text-white/70 font-sans">info@vs-group.dk</p>
                </div>
              </div>
            </div>

            <div className="pt-4">
              
              
            </div>
          </div>

          {}
          
        </div>
      </div>
    </section>;
}